import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Search } from 'lucide-react';
import {
  SEARCH_MIN_LENGTH,
  type FieldEngineer,
  type FieldJob,
  type FieldTargetType,
  type SearchResults,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { Button, Field, Input, Spinner } from '../../components/ui';

/**
 * Raise a deployment job: what, where, and who.
 *
 * The target is PICKED, never typed — the same universal search the header box
 * uses, narrowed to the kind chosen — so a job names a real ONU or FAT rather
 * than a row id somebody pasted. The assignee list is only people whose role
 * can actually open a job (the server's `field.deploy` rule, surfaced here —
 * engineers, and since 2026-09-11 technicians), so a dispatcher cannot strand
 * a job on somebody who will never see it.
 *
 * Raising closes the dialog at once and the write runs in the tasks tray; the
 * board refreshes when it lands. The searches above it are reads and stay
 * here. (FieldJobDetailDialog is the deliberate exception — see its docblock.)
 */

const TARGET_TYPES: Array<{ value: FieldTargetType; label: string }> = [
  { value: 'onu', label: 'ONU' },
  { value: 'splitter', label: 'Splitter · FAT · ODF' },
  { value: 'zone', label: 'Zone' },
];

export function RaiseFieldJobDialog({
  engineers,
  onClose,
}: {
  engineers: FieldEngineer[];
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [title, setTitle] = useState('');
  const [targetType, setTargetType] = useState<FieldTargetType>('onu');
  const [term, setTerm] = useState('');
  const [target, setTarget] = useState<{ id: string; label: string } | null>(null);
  const [assignedTo, setAssignedTo] = useState('');

  const search = useQuery({
    queryKey: ['search', term],
    queryFn: () => api.get<SearchResults>('/search', { q: term }),
    enabled: term.trim().length >= SEARCH_MIN_LENGTH,
  });
  const hits = useMemo(() => {
    const group = search.data?.groups.find((g) => g.type === targetType);
    return group?.hits ?? [];
  }, [search.data, targetType]);

  const ready = title.trim().length > 0 && target !== null && assignedTo !== '';

  const create = () => {
    if (!target) return;
    // Snapshotted: the dialog is gone by the time the write settles.
    const body = { title: title.trim(), targetType, targetId: target.id, assignedTo };
    const engineer = engineers.find((e) => e.id === assignedTo)?.name ?? 'the person it is assigned to';
    onClose();
    tasks.start<FieldJob>({
      label: `Raise job — ${body.title}`,
      run: () => api.post<FieldJob>('/field/jobs', body),
      done: () => `“${body.title}” raised and assigned to ${engineer}.`,
      invalidate: [['field', 'jobs']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Raise a deployment job</h2>
          <p className="mt-0.5 text-xs text-muted">
            Somebody carries this into the field and brings back a measured fix.
          </p>
        </header>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
          <Field label="What is being done" hint="Said the way you would read it out, e.g. “Install FAT Kikuyu-3”.">
            <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="Install ONU — Wanjiru, house 42" />
          </Field>

          <Field label="Kind of equipment">
            <div className="flex flex-wrap gap-2">
              {TARGET_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => {
                    setTargetType(t.value);
                    setTarget(null);
                  }}
                  className={`rounded-lg border px-3 py-1.5 text-sm ${
                    targetType === t.value
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-line text-muted hover:text-ink'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          {target ? (
            <Field label="Equipment">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm">
                <span className="min-w-0 truncate text-ink">{target.label}</span>
                <button type="button" className="shrink-0 text-xs text-link hover:underline" onClick={() => setTarget(null)}>
                  Change
                </button>
              </div>
            </Field>
          ) : (
            <Field label="Find the equipment" hint="Search by name or serial — pick the real one, don't type an id.">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-faint" />
                <Input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={`Search ${TARGET_TYPES.find((t) => t.value === targetType)?.label ?? ''}…`}
                  className="pl-8"
                  autoFocus
                />
              </div>
              {term.trim().length >= SEARCH_MIN_LENGTH && (
                <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-line">
                  {search.isLoading ? (
                    <div className="flex justify-center p-4">
                      <Spinner />
                    </div>
                  ) : hits.length === 0 ? (
                    <p className="px-3 py-4 text-center text-sm text-muted">
                      Nothing matched. Try the name or serial as it reads on the equipment.
                    </p>
                  ) : (
                    <ul className="divide-y divide-line">
                      {hits.map((h) => (
                        <li key={h.id}>
                          <button
                            type="button"
                            onClick={() => setTarget({ id: h.id, label: h.title })}
                            className="flex w-full flex-col items-start px-3 py-2 text-left transition-colors hover:bg-surface-2"
                          >
                            <span className="text-sm text-ink">{h.title}</span>
                            {h.subtitle && <span className="text-xs text-muted">{h.subtitle}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Field>
          )}

          <Field label="Assign to" hint="Only people whose role can work a deployment job.">
            {engineers.length === 0 ? (
              <p className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                No one in this organization can work a deployment job yet. Grant “Work deployment
                jobs in the field” to a role on the Roles page, then raise the job.
              </p>
            ) : (
              <select className="input" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">Choose a person…</option>
                {engineers.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name} — {e.email}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" icon={<ArrowLeft className="size-4" />} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={create}>
            Create
          </Button>
        </footer>
      </div>
    </div>
  );
}
